import {
  LiveQuizQuestionSession,
  LiveQuizSession,
} from '@protos/learning_management/lms.db_pb';
import {
  LiveQuizQuestionStats,
  LiveQuizRosterEntry,
  LiveQuizSessionAnalytics,
} from '@protos/learning_management/lms.liveQuiz.teacher.apis_pb';
import { Question } from '@protos/content_management/content.db_pb';
import { createSlice } from '@reduxjs/toolkit';

export interface ILiveQuizParticipant {
  studentId: string;
  name: string;
  rollNumber?: string;
  profilePicUrl?: string;
  joinedAt?: unknown;
  isOnline?: boolean;
}

export interface ILiveQuizAnswerStatus {
  isCorrect?: boolean;
}

export interface ILiveQuizTeacherState {
  live_quiz_session?: LiveQuizSession;
  question_sessions: LiveQuizQuestionSession[];
  questions: Question[];
  firestore_custom_token?: string;
  roster?: LiveQuizRosterEntry[];
  joined_participants: ILiveQuizParticipant[];
  current_question_session_id?: string;
  current_question_status?:
    | 'SESSION_STATUS_STARTED'
    | 'SESSION_STATUS_COMPLETED';
  current_question_responses: Record<string, ILiveQuizAnswerStatus>;
  last_question_stats?: LiveQuizQuestionStats;
  analytics?: LiveQuizSessionAnalytics;
}

const initialState: ILiveQuizTeacherState = {
  question_sessions: [],
  questions: [],
  joined_participants: [],
  current_question_responses: {},
};

export const liveQuizSlice = createSlice({
  name: 'liveQuiz',
  initialState,
  reducers: {
    setLiveQuizSession: (
      state,
      action: { payload: LiveQuizSession | undefined }
    ) => {
      state.live_quiz_session = action.payload;
    },
    setQuestionSessions: (
      state,
      action: { payload: LiveQuizQuestionSession[] }
    ) => {
      state.question_sessions = action.payload;
    },
    setLiveQuizQuestions: (state, action: { payload: Question[] }) => {
      state.questions = action.payload;
    },
    setFirestoreCustomToken: (
      state,
      action: { payload: string | undefined }
    ) => {
      state.firestore_custom_token = action.payload;
    },
    setLiveQuizRoster: (
      state,
      action: { payload: LiveQuizRosterEntry[] | undefined }
    ) => {
      state.roster = action.payload;
    },
    setJoinedParticipants: (
      state,
      action: { payload: ILiveQuizParticipant[] }
    ) => {
      state.joined_participants = action.payload;
    },
    setCurrentQuestionSessionId: (
      state,
      action: { payload: string | undefined }
    ) => {
      state.current_question_session_id = action.payload;
    },
    setCurrentQuestionStatus: (
      state,
      action: { payload: ILiveQuizTeacherState['current_question_status'] }
    ) => {
      state.current_question_status = action.payload;
    },
    setCurrentQuestionResponses: (
      state,
      action: { payload: Record<string, ILiveQuizAnswerStatus> }
    ) => {
      state.current_question_responses = action.payload;
    },
    setLastQuestionStats: (
      state,
      action: { payload: LiveQuizQuestionStats | undefined }
    ) => {
      state.last_question_stats = action.payload;
    },
    setLiveQuizAnalytics: (
      state,
      action: { payload: LiveQuizSessionAnalytics | undefined }
    ) => {
      state.analytics = action.payload;
    },
    resetLiveQuiz: () => initialState,
  },
});

export const {
  setLiveQuizSession,
  setQuestionSessions,
  setLiveQuizQuestions,
  setFirestoreCustomToken,
  setLiveQuizRoster,
  setJoinedParticipants,
  setCurrentQuestionSessionId,
  setCurrentQuestionStatus,
  setCurrentQuestionResponses,
  setLastQuestionStats,
  setLiveQuizAnalytics,
  resetLiveQuiz,
} = liveQuizSlice.actions;

export default liveQuizSlice.reducer;
